import { useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Tooltip,
  Tag,
  Segmented,
} from "antd";
import { Edit3, FlaskConical, Trash2, PlusCircle, MinusCircle, Plus } from "lucide-react";
import {
  SAMPLING_TYPE_OPTIONS,
  useSamplingTypeConfig,
} from "@/hooks/useAppSetting";

const { TextArea } = Input;

const typeOption = (type) => SAMPLING_TYPE_OPTIONS.find((o) => o.value === type);

const FREQUENCY_FROM_OPTIONS = [
  { value: "FIRST_SAMPLE", label: "After first sample" },
  { value: "OPERATION_START", label: "From operation start" },
];

// Extra fields the recipe designer adds to a sample: just a label and a value.
// A value filled in here is shown to the operator as fixed information; a field
// left without a value is filled in by the operator during execution.
const newCustomField = () => ({
  key: `cf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
  label: "",
  value: "",
});

// Items saved before sampling types existed carry is_qc_sampling only — map them.
const typeOfItem = (item) => {
  if (item?.sampling_type) return item.sampling_type;
  const isSavedItem =
    item &&
    (item.is_qc_sampling !== undefined || item.sample_id || item.sample || item.sample_name);
  if (!isSavedItem) return null; // brand-new form: nothing chosen yet
  return item.is_qc_sampling ? "QC" : "INLINE";
};

const DEFAULT_VALUES = {
  sample_name: "",
  sampling_type: null,
  quantity: null,
  unit: "",
  test_type_id: null,
  parameter_id: null,
  min_value: null,
  max_value: null,
  description: null,
  sample_instruction: null,
  max_samples_mode: "COUNT",
  max_samples: 1,
  sample_frequency_min: null,
  // what the frequency is counted from: the operation start, or the first sample
  sample_frequency_from: "FIRST_SAMPLE",
  // a sample is on time when taken within +/- this many minutes of its due time
  sample_frequency_tolerance_min: null,
  custom_fields: [],
  sampling_min_value: null,
  sampling_max_value: null,
};

const validationSchema = Yup.object().shape({
  sample_name: Yup.string().trim().nullable().required("Sample name is required"),
  sampling_type: Yup.string().nullable().required("Sampling type is required"),
  quantity: Yup.number()
    .nullable()
    .required("Quantity is required")
    .moreThan(0, "Quantity must be greater than 0"),
  test_type_id: Yup.string().nullable().required("Test type is required"),
  max_samples: Yup.number()
    .nullable()
    .when("max_samples_mode", {
      is: "COUNT",
      then: (schema) =>
        schema
          .required("Number of samples is required")
          .integer("Whole number only")
          .min(1, "At least 1"),
    }),
  sample_frequency_min: Yup.number().nullable().min(0, "Cannot be negative"),
  sample_frequency_tolerance_min: Yup.number()
    .nullable()
    .min(0, "Cannot be negative")
    .when("sample_frequency_min", {
      is: (freq) => freq !== null && freq !== undefined && freq > 0,
      then: (schema) =>
        schema.max(
          Yup.ref("sample_frequency_min"),
          "Tolerance cannot be more than the frequency"
        ),
    }),
  parameter_id: Yup.string().nullable(),
  min_value: Yup.number()
    .nullable()
    .when("parameter_id", {
      is: (val) => val !== null && val !== undefined && val !== "",
      then: (schema) =>
        schema.required("Min value is required when parameter is selected"),
    }),
  max_value: Yup.number()
    .nullable()
    .when(["parameter_id", "min_value"], {
      is: (parameter_id, min_value) =>
        parameter_id !== null &&
        parameter_id !== undefined &&
        parameter_id !== "" &&
        min_value !== null &&
        min_value !== undefined,
      then: (schema) =>
        schema
          .required("Max value is required when parameter is selected")
          .min(
            Yup.ref("min_value"),
            "Max value must be greater than or equal to min value"
          ),
    }),
  description: Yup.string()
    .nullable()
    .when("parameter_id", {
      is: (val) => val !== null && val !== undefined && val !== "",
      then: (schema) =>
        schema.required("Description is required when parameter is selected"),
    }),
  sample_instruction: Yup.string().nullable(),
  custom_fields: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().trim().required("Label is required"),
        value: Yup.string().nullable(),
      })
    )
    .test("unique-labels", "Field labels must be unique", (arr) => {
      const labels = (arr || []).map((f) => (f?.label || "").trim().toLowerCase());
      return new Set(labels).size === labels.length;
    }),
  sampling_min_value: Yup.number()
    .nullable()
    .when("sampling_type", {
      is: "QC",
      then: (schema) => schema.required("Min value is required for QC sampling"),
    }),
  sampling_max_value: Yup.number()
    .nullable()
    .when(["sampling_type", "sampling_min_value"], {
      is: (sampling_type, sampling_min_value) =>
        sampling_type === "QC" &&
        sampling_min_value !== null &&
        sampling_min_value !== undefined,
      then: (schema) =>
        schema
          .required("Max value is required for QC sampling")
          .min(
            Yup.ref("sampling_min_value"),
            "Max value must be greater than or equal to min value"
          ),
    }),
});

// Who samples / tests / decides for a sampling type — read from Application Settings.
function SamplingRolesInfo({ type }) {
  const module = typeOption(type)?.module || "";
  const cfg = useSamplingTypeConfig(module);
  if (!type) return null;
  const role = (v) => (
    <b>{v || <span className="text-gray-400 font-normal">not set</span>}</b>
  );
  return (
    <div
      className="rounded-md border px-3 py-2 text-xs leading-5"
      style={{ backgroundColor: "#f9fafb", borderColor: "#e5e7eb" }}
    >
      <span className="text-gray-500">Set in Application Settings — </span>
      Sample taken by {role(cfg.sampled_by)} · Sent to / tested by{" "}
      {role(cfg.sent_to)} · OK / Not OK by {role(cfg.decided_by)}
    </div>
  );
}

export default function SamplingForm({
  editIndex,
  onSubmit = (values) => console.log("Submitted values:", values),
  testing_type_list = [],
  parameter_list = [],
  // Quantity is in the recipe's batch size unit.
  batchUnit = "",
  values = DEFAULT_VALUES,
}) {
  const initialValues = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      ...values,
      // old items: the sample was a material name; type was only QC / inline
      sample_name: values?.sample_name ?? values?.sample ?? "",
      sampling_type: typeOfItem(values),
    }),
    [values]
  );
  const [showParam, setShowParam] = useState(!!values?.parameter_id);

  const handleSubmit = (vals, helpers) =>
    onSubmit(
      {
        ...vals,
        sample: vals.sample_name,
        unit: batchUnit || vals.unit || "",
        // IPC and QC samples are both tested by the QC lab; inline are tested in place.
        is_qc_sampling: vals.sampling_type !== "INLINE",
        max_samples: vals.max_samples_mode === "UNTIL_PASS" ? null : vals.max_samples,
        sample_frequency_from: vals.sample_frequency_min
          ? vals.sample_frequency_from || "FIRST_SAMPLE"
          : "FIRST_SAMPLE",
        sample_frequency_tolerance_min: vals.sample_frequency_min
          ? vals.sample_frequency_tolerance_min
          : null,
        custom_fields: (vals.custom_fields || []).map((f) => ({
          key: f.key,
          label: (f.label || "").trim(),
          value: (f.value || "").trim(),
        })),
      },
      helpers
    );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit, submitCount }) => {
        const err = (f) => (touched[f] && errors[f] ? "error" : "");
        const help = (f) => touched[f] && errors[f];
        const showLimits = values.sampling_type && values.sampling_type !== "INLINE";

        return (
          <Form layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              {/* Sample name — free text, what the plant calls it */}
              <Col span={8}>
                <Form.Item
                  label="Sample Name"
                  validateStatus={err("sample_name")}
                  help={help("sample_name")}
                >
                  <Input
                    placeholder="e.g. Reaction mass sample"
                    value={values.sample_name}
                    onChange={(e) => setFieldValue("sample_name", e.target.value)}
                  />
                </Form.Item>
              </Col>

              {/* Sampling type */}
              <Col span={8}>
                <Form.Item
                  label="Sampling Type"
                  validateStatus={err("sampling_type")}
                  help={help("sampling_type")}
                >
                  <Select
                    value={values.sampling_type}
                    placeholder="Select Sampling Type"
                    onChange={(val) => {
                      setFieldValue("sampling_type", val || null);
                      if (val === "INLINE") {
                        setFieldValue("sampling_min_value", null);
                        setFieldValue("sampling_max_value", null);
                      }
                    }}
                    options={SAMPLING_TYPE_OPTIONS.map((o) => ({
                      label: o.label,
                      value: o.value,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* Quantity — in the batch size unit */}
              <Col span={8}>
                <Form.Item
                  label="Quantity"
                  validateStatus={err("quantity")}
                  help={help("quantity")}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Quantity"
                    value={values.quantity}
                    min={0}
                    step="any"
                    addonAfter={batchUnit || values.unit || "Unit"}
                    onChange={(val) => setFieldValue("quantity", val)}
                  />
                </Form.Item>
              </Col>

              {/* Roles for the chosen type (from Application Settings) */}
              {values.sampling_type && (
                <Col span={24}>
                  <Form.Item>
                    <SamplingRolesInfo type={values.sampling_type} />
                  </Form.Item>
                </Col>
              )}

              {/* Test Type */}
              <Col span={8}>
                <Form.Item
                  label="Test Type"
                  validateStatus={err("test_type_id")}
                  help={help("test_type_id")}
                >
                  <Select
                    value={values.test_type_id}
                    placeholder="Select Test Type"
                    onChange={(val, option) => {
                      setFieldValue("test_type_id", val || null);
                      setFieldValue("test_type", val ? option.label : null);
                    }}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={testing_type_list?.map((test) => ({
                      label: test.testing_type,
                      value: test.testing_type_id,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* Result limits — IPC / QC samples are tested by the lab */}
              {showLimits && (
                <>
                  <Col span={8}>
                    <Form.Item
                      label="Result Min Value"
                      validateStatus={err("sampling_min_value")}
                      help={help("sampling_min_value")}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Min value"
                        step="any"
                        value={values.sampling_min_value}
                        onChange={(val) => setFieldValue("sampling_min_value", val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Result Max Value"
                      validateStatus={err("sampling_max_value")}
                      help={help("sampling_max_value")}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Max value"
                        step="any"
                        value={values.sampling_max_value}
                        onChange={(val) => setFieldValue("sampling_max_value", val)}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {/* How many samples can be taken, and how often */}
              <Col span={8}>
                <Form.Item label="Max Samples">
                  <Segmented
                    block
                    value={values.max_samples_mode}
                    options={[
                      { label: "Number", value: "COUNT" },
                      { label: "Until pass", value: "UNTIL_PASS" },
                    ]}
                    onChange={(val) => setFieldValue("max_samples_mode", val)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                {values.max_samples_mode === "COUNT" ? (
                  <Form.Item
                    label="Number of Samples"
                    validateStatus={err("max_samples")}
                    help={help("max_samples")}
                  >
                    <InputNumber
                      className="w-full"
                      min={1}
                      precision={0}
                      placeholder="Max samples"
                      value={values.max_samples}
                      onChange={(val) => setFieldValue("max_samples", val)}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Number of Samples">
                    <div className="text-xs text-gray-500 pt-1.5">
                      No limit — sampling repeats until the result passes.
                    </div>
                  </Form.Item>
                )}
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Frequency Between Samples (optional)"
                  validateStatus={err("sample_frequency_min")}
                  help={help("sample_frequency_min")}
                >
                  <InputNumber
                    className="w-full"
                    min={0}
                    step="any"
                    placeholder="Gap between samples"
                    addonAfter="min"
                    value={values.sample_frequency_min}
                    onChange={(val) => setFieldValue("sample_frequency_min", val)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Frequency Counted From"
                  tooltip="After first sample: the first sample is taken when needed, then repeated at this gap. From operation start: samples are due at this gap after the operation starts."
                >
                  <Select
                    value={values.sample_frequency_from}
                    disabled={!values.sample_frequency_min}
                    onChange={(val) => setFieldValue("sample_frequency_from", val)}
                    options={FREQUENCY_FROM_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Frequency Tolerance (±)"
                  tooltip="A sample counts as on time when it is taken within this many minutes before or after its due time."
                  validateStatus={err("sample_frequency_tolerance_min")}
                  help={help("sample_frequency_tolerance_min")}
                >
                  <InputNumber
                    className="w-full"
                    min={0}
                    step="any"
                    placeholder="e.g. 10"
                    addonAfter="min"
                    disabled={!values.sample_frequency_min}
                    value={values.sample_frequency_tolerance_min}
                    onChange={(val) => setFieldValue("sample_frequency_tolerance_min", val)}
                  />
                </Form.Item>
              </Col>

              {/* Conditional Parameter (collapsible, same design as ingredients) */}
              <Col span={24}>
                <div className="mt-1 pt-3 mb-3" style={{ borderTop: "1px solid #f0f0f0" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider m-0">
                      Conditional Parameter
                    </p>
                    <button
                      type="button"
                      style={{
                        width: 22,
                        height: 22,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "9999px",
                        backgroundColor: showParam ? "#fee2e2" : "#dbeafe",
                        border: `2px solid ${showParam ? "#f87171" : "#3b82f6"}`,
                        cursor: "pointer",
                        outline: "none",
                        transition: "background-color 0.2s, border-color 0.2s",
                      }}
                      onClick={() => {
                        if (showParam) {
                          setFieldValue("parameter_id", null);
                          setFieldValue("parameter", null);
                          setFieldValue("parameter_unit", null);
                          setFieldValue("min_value", null);
                          setFieldValue("max_value", null);
                        }
                        setShowParam(!showParam);
                      }}
                    >
                      {showParam ? (
                        <MinusCircle size={14} style={{ color: "#ef4444" }} />
                      ) : (
                        <PlusCircle size={14} style={{ color: "#3b82f6" }} />
                      )}
                    </button>
                  </div>
                  {showParam && (
                    <Row gutter={16}>
                      <Col span={values.parameter_id ? 16 : 24}>
                        <Form.Item
                          label="Parameter"
                          validateStatus={err("parameter_id")}
                          help={help("parameter_id")}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            value={values.parameter_id}
                            onChange={(val, option) => {
                              setFieldValue("parameter_id", val || null);
                              if (val) {
                                setFieldValue("parameter", option.label || null);
                                setFieldValue("parameter_unit", option.parameter_unit);
                              } else {
                                setFieldValue("parameter", null);
                                setFieldValue("parameter_unit", null);
                                setFieldValue("min_value", null);
                                setFieldValue("max_value", null);
                              }
                            }}
                            showSearch
                            placeholder="Select Parameter"
                            allowClear
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            options={parameter_list?.map((param) => ({
                              value: param.id,
                              label: param.parameter_name,
                              parameter_unit: param.unit,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      {values.parameter_id && (
                        <>
                          <Col span={4}>
                            <Form.Item
                              label="Min"
                              validateStatus={err("min_value")}
                              help={help("min_value")}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                className="w-full"
                                placeholder="Min"
                                step="any"
                                value={values.min_value}
                                onChange={(val) => setFieldValue("min_value", val)}
                                addonAfter={
                                  <Tooltip title={values.parameter_unit}>
                                    <span>
                                      {values.parameter_unit?.length > 4
                                        ? values.parameter_unit?.slice(0, 4) + "..."
                                        : values.parameter_unit}
                                    </span>
                                  </Tooltip>
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              label="Max"
                              validateStatus={err("max_value")}
                              help={help("max_value")}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                className="w-full"
                                placeholder="Max"
                                step="any"
                                value={values.max_value}
                                onChange={(val) => setFieldValue("max_value", val)}
                                addonAfter={
                                  <Tooltip title={values.parameter_unit}>
                                    <span>
                                      {values.parameter_unit?.length > 4
                                        ? values.parameter_unit?.slice(0, 4) + "..."
                                        : values.parameter_unit}
                                    </span>
                                  </Tooltip>
                                }
                              />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </Row>
                  )}
                </div>
              </Col>

              {/* Custom fields: label + value. Value set = fixed info; empty = operator fills it */}
              <Col span={24}>
                <div className="pt-3 mb-3" style={{ borderTop: "1px solid #f0f0f0" }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider m-0">
                      Custom Fields
                    </p>
                    <Button
                      type="dashed"
                      size="small"
                      icon={<Plus size={12} />}
                      onClick={() =>
                        setFieldValue("custom_fields", [
                          ...(values.custom_fields || []),
                          newCustomField(),
                        ])
                      }
                    >
                      Add Field
                    </Button>
                  </div>
                  {typeof errors.custom_fields === "string" && submitCount > 0 && (
                    <div className="text-xs text-red-500 mb-2">{errors.custom_fields}</div>
                  )}
                  {(values.custom_fields || []).length === 0 ? (
                    <div className="text-xs text-gray-500">
                      No custom fields. Leave a field's value empty if the operator should fill
                      it during execution.
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mb-2">
                      Leave the value empty if the operator should fill it during execution.
                    </div>
                  )}
                  {(values.custom_fields || []).map((f, i) => {
                    const rowErr = (Array.isArray(errors.custom_fields) && errors.custom_fields[i]) || {};
                    const update = (patch) =>
                      setFieldValue(
                        "custom_fields",
                        values.custom_fields.map((x, idx) => (idx === i ? { ...x, ...patch } : x))
                      );
                    return (
                      <Row key={f.key} gutter={12} align="top" className="mb-1">
                        <Col span={10}>
                          <Form.Item
                            validateStatus={submitCount > 0 && rowErr.label ? "error" : ""}
                            help={submitCount > 0 && rowErr.label}
                            style={{ marginBottom: 8 }}
                          >
                            <Input
                              placeholder="Label (e.g. Sample container no.)"
                              value={f.label}
                              onChange={(e) => update({ label: e.target.value })}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item style={{ marginBottom: 8 }}>
                            <Input
                              placeholder="Value (leave empty for the operator to fill)"
                              value={f.value}
                              onChange={(e) => update({ value: e.target.value })}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2} className="flex justify-end pt-1">
                          <Tooltip title="Remove field">
                            <button
                              type="button"
                              className="p-1.5 rounded-md hover:bg-red-50"
                              onClick={() =>
                                setFieldValue(
                                  "custom_fields",
                                  values.custom_fields.filter((_, idx) => idx !== i)
                                )
                              }
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </Tooltip>
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              </Col>

              {/* Description */}
              <Col span={8}>
                <Form.Item
                  label="Description"
                  validateStatus={err("description")}
                  help={help("description")}
                >
                  <TextArea
                    rows={2}
                    placeholder="Description"
                    value={values.description}
                    onChange={(e) => setFieldValue("description", e.target.value)}
                  />
                </Form.Item>
              </Col>

              {/* Sample Instruction */}
              <Col span={16}>
                <Form.Item
                  label="Sample Instruction"
                  validateStatus={err("sample_instruction")}
                  help={help("sample_instruction")}
                >
                  <TextArea
                    rows={2}
                    placeholder="Instruction"
                    value={values.sample_instruction}
                    onChange={(e) => setFieldValue("sample_instruction", e.target.value)}
                  />
                </Form.Item>
              </Col>

              {/* Submit Button */}
              <Col span={24}>
                <Button type="primary" htmlType="submit" block>
                  {editIndex !== null ? "Update" : "Add"}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
}

export const SamplingCard = ({ fromView, item, onEdit, onDelete }) => {
  const {
    sample,
    sample_name,
    sample_id,
    quantity,
    unit,
    test_type,
    test_type_id,
    parameter,
    parameter_id,
    parameter_unit,
    min_value,
    max_value,
    description,
    sample_instruction,
    max_samples,
    max_samples_mode,
    sample_frequency_min,
    sample_frequency_from,
    sample_frequency_tolerance_min,
    custom_fields,
    sampling_min_value,
    sampling_max_value,
  } = item;

  const type = typeOfItem(item) || "INLINE";
  const label = typeOption(type)?.label || "Inline";
  const hasLimits = type !== "INLINE";

  const showLabel = (l, id) => l || `${id ? "ID: " + id : "N/A"}`;
  const name = sample_name || sample;
  const samplesText =
    max_samples_mode === "UNTIL_PASS"
      ? "Until pass"
      : max_samples
      ? `Up to ${max_samples}`
      : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition p-3 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-pink-100 rounded-md">
            <FlaskConical className="w-4 h-4 text-pink-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p
                className="font-semibold text-gray-900 leading-tight truncate text-base w-48"
                title={showLabel(name, sample_id)}
              >
                {showLabel(name, sample_id)}
              </p>
              <Tag
                color={type === "QC" ? "gold" : type === "IPC" ? "blue" : "default"}
                className="text-xs font-medium shrink-0"
              >
                {label.toUpperCase()}
              </Tag>
            </div>
            {(samplesText || sample_frequency_min) && (
              <p className="text-xs text-gray-600 truncate">
                {samplesText && `Samples: ${samplesText}`}
                {samplesText && sample_frequency_min ? " · " : ""}
                {sample_frequency_min
                  ? `Every ${sample_frequency_min} min${
                      sample_frequency_tolerance_min
                        ? ` (±${sample_frequency_tolerance_min})`
                        : ""
                    } ${
                      sample_frequency_from === "OPERATION_START"
                        ? "from operation start"
                        : "after first sample"
                    }`
                  : ""}
              </p>
            )}
          </div>
        </div>

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit sample"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete sample"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-indigo-50 rounded-md p-2.5">
          <p className="text-xs text-indigo-700 font-medium mb-1">Quantity</p>
          <p className="text-indigo-900 font-semibold">
            {quantity}{" "}
            <span className="text-sm font-normal text-indigo-700">
              {unit || "Unit"}
            </span>
          </p>
        </div>

        <div className="bg-purple-50 rounded-md p-2.5">
          <p className="text-xs text-purple-700 font-medium mb-1">Test Type</p>
          <p
            className="text-purple-900 font-semibold truncate"
            title={showLabel(test_type, test_type_id)}
          >
            {showLabel(test_type, test_type_id)}
          </p>
        </div>

        {(parameter || parameter_id) && (
          <div className="bg-green-50 rounded-md p-2.5">
            <p className="text-xs text-green-700 font-medium mb-1">Parameter</p>
            <div className="flex justify-between items-center">
              <p
                className="text-green-900 font-semibold truncate mr-2"
                title={showLabel(parameter, parameter_id)}
              >
                {showLabel(parameter, parameter_id)}
              </p>
              <span className="text-xs text-green-700 font-medium bg-green-100 px-1.5 py-0.5 rounded">
                {parameter_unit || "N/A"}
              </span>
            </div>
          </div>
        )}

        {((min_value !== null && min_value !== undefined) ||
          (max_value !== null && max_value !== undefined)) && (
            <div className="bg-orange-50 rounded-md p-2.5">
              <p className="text-xs text-orange-700 font-medium mb-1">Range</p>
              <p className="text-orange-900 font-semibold">
                {min_value !== null ? min_value : "N/A"} -{" "}
                {max_value !== null ? max_value : "N/A"}{" "}
                <span className="text-sm font-normal text-orange-700">
                  {parameter_unit}
                </span>
              </p>
            </div>
          )}

        {/* Result limits — IPC / QC samples */}
        {hasLimits &&
          ((sampling_min_value !== null && sampling_min_value !== undefined) ||
            (sampling_max_value !== null && sampling_max_value !== undefined)) && (
            <div className="bg-yellow-50 rounded-md p-2.5 col-span-2">
              <p className="text-xs text-yellow-700 font-medium mb-1">Result Limits</p>
              <p className="text-yellow-900 font-semibold">
                {sampling_min_value !== null && sampling_min_value !== undefined
                  ? sampling_min_value
                  : "N/A"}
                {" – "}
                {sampling_max_value !== null && sampling_max_value !== undefined
                  ? sampling_max_value
                  : "N/A"}
              </p>
            </div>
          )}
      </div>

      {/* Custom fields */}
      {Array.isArray(custom_fields) && custom_fields.length > 0 && (
        <div className="mt-3 bg-slate-50 rounded-md p-2.5">
          <p className="text-xs text-slate-600 font-medium mb-1">Custom Fields</p>
          <div className="flex flex-col gap-0.5">
            {custom_fields.map((f) => (
              <p key={f.key} className="text-sm text-slate-800 m-0">
                <span className="font-medium">{f.label}</span>
                <span className="text-slate-500">
                  {" — "}
                  {f.value ? f.value : "operator fills"}
                </span>
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {description && (
        <div className="mt-3 bg-gray-50 rounded-md p-2.5">
          <p className="text-xs text-gray-600 font-medium mb-1">Description</p>
          <p className="text-gray-800 text-sm leading-relaxed">{description}</p>
        </div>
      )}

      {/* Instruction */}
      {sample_instruction && (
        <div className="mt-3 bg-gray-50 rounded-md p-2.5">
          <p className="text-xs text-gray-600 font-medium mb-1">Instruction</p>
          <p className="text-gray-800 italic text-sm leading-relaxed">
            {sample_instruction}
          </p>
        </div>
      )}
    </div>
  );
};
